import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, money, inputCls, Field, Modal, PageHead, PrimaryBtn, FinanceEmpty, EXPENSE_CATS } from './shared';
import Icon from '../../../components/dashboard/DashboardIcons';

export default function BudgetPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ category: 'equipment', allocated_amount: '' });
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setBudgets((await axios.get(`${API}/budgets?year=${year}`, auth())).data || []); }
    catch (e) { setError(e.response?.status === 403 ? 'لا تملك صلاحية الوصول للميزانية' : 'تعذر التحميل'); }
    setLoading(false);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.allocated_amount) return;
    try {
      await axios.post(`${API}/budgets`, { year, category: form.category, allocated_amount: parseFloat(form.allocated_amount) }, auth());
      setModal(false); notify('تم حفظ بند الميزانية'); load();
    } catch { notify('فشل الحفظ'); }
  };

  const [confirmDel, setConfirmDel] = useState(null);
  const remove = async () => {
    try { await axios.delete(`${API}/budgets/${confirmDel.id}`, auth()); notify('تم الحذف'); setConfirmDel(null); load(); }
    catch { notify('فشل الحذف'); setConfirmDel(null); }
  };

  if (error) return <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-600" data-testid="budget-error">{error}</div>;
  const totalAlloc = budgets.reduce((s, b) => s + b.allocated_amount, 0);
  const totalSpent = budgets.reduce((s, b) => s + b.spent_amount, 0);

  return (
    <div className="mx-auto max-w-5xl space-y-4" data-testid="budget-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="الميزانية" desc="الميزانيات المعتمدة مقابل المصروف الفعلي (المصروفات المعتمدة والمدفوعة)"
        action={<div className="flex gap-2">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="budget-year-select">
            {[year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <PrimaryBtn onClick={() => setModal(true)} title="إضافة أو تحديث بند" data-testid="budget-add-btn"><Icon name="plus" className="w-4 h-4" /> بند ميزانية</PrimaryBtn>
        </div>} />

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">إجمالي المعتمد</div><div className="text-xl font-bold text-slate-900" dir="rtl" data-testid="budget-total-alloc">{money(totalAlloc)}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">إجمالي المصروف</div><div className="text-xl font-bold text-red-600" dir="rtl">{money(totalSpent)}</div></div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        {loading ? <div className="h-24 animate-pulse rounded-lg bg-slate-100" />
          : budgets.length === 0 ? <FinanceEmpty text={`لا توجد بنود ميزانية لسنة ${year} — أضف أول بند`} />
          : (
            <div className="space-y-4">
              {budgets.map((b) => (
                <div key={b.id} data-testid="budget-row">
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-800">{EXPENSE_CATS[b.category] || b.category}</span>
                    <div className="flex items-center gap-2">
                      {b.usage_percent > 100 && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600" data-testid="budget-over-alert">تجاوز الميزانية</span>}
                      <span className="text-xs text-slate-500" dir="rtl">{money(b.spent_amount)} / {money(b.allocated_amount)} ({b.usage_percent}%)</span>
                      <button onClick={() => setConfirmDel(b)} className="text-slate-400 hover:text-red-600" data-testid="budget-del-btn"><Icon name="x" className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full ${b.usage_percent > 100 ? 'bg-red-500' : b.usage_percent > 80 ? 'bg-amber-400' : 'bg-teal-500'}`} style={{ width: `${Math.min(100, b.usage_percent)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <Modal open={Boolean(confirmDel)} title="حذف بند الميزانية" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">هل أنت متأكد من حذف بند «{confirmDel ? (EXPENSE_CATS[confirmDel.category] || confirmDel.category) : ''}»؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="budget-confirm-del">حذف</button>
        </div>
      </Modal>

      <Modal open={modal} title={`بند ميزانية ${year}`} onClose={() => setModal(false)}>
        <form onSubmit={submit} className="space-y-3">
          <Field label="التصنيف (إن كان موجوداً سيتم تحديث مبلغه)" required><select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="budget-input-category">{Object.entries(EXPENSE_CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="المبلغ المعتمد (ر.س)" required><input className={inputCls} dir="ltr" type="number" min="1" value={form.allocated_amount} onChange={(e) => setForm({ ...form, allocated_amount: e.target.value })} data-testid="budget-input-amount" /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="budget-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
